"use client"

import { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import CardImage2 from "./CardImage2";
import { cards } from '@/constants/cards.json';
import { useCardTranslations } from "@/utils/cardTranslations";
import { Button } from "@mui/material";
import { Shuffle } from "lucide-react";
import CardsContext, { TarotCard } from '@/context/CardsContext';
import { track } from '@/third-party/MixPanel';
import { useTranslation } from 'react-i18next';

interface CardSelectionProps {
    onSelectionComplete?: (selectedCards: TarotCard[]) => void;
    maxSelections: number;
}

const CardSelection = ({ onSelectionComplete, maxSelections }: CardSelectionProps) => {
    const { t } = useTranslation();
    const cardsContext = useContext(CardsContext);
    if (!cardsContext) {
        throw new Error('CardSelection must be used within a CardsContext Provider');
    }
    const { getTranslatedCard } = cardsContext;
    const { getCardName } = useCardTranslations();
    const [selectedCards, setSelectedCards] = useState<Set<number>>(new Set());
    const [cardReversedState, setCardReversedState] = useState<Map<number, boolean>>(new Map());
    const [cardOrder, setCardOrder] = useState(Array.from({ length: 78 }, (_, i) => i + 1));
    const [isShuffling, setIsShuffling] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 412);

    // Calculate card width based on screen width (65px is reference for 412px screen)
    const cardWidth = Math.round((windowWidth * 60) / 412);
    const cardHeight = Math.round(cardWidth * 1.69); // Maintain aspect ratio
    const overlapX = Math.round(cardWidth * -0.49); // Maintain proportional overlap
    const overlapY = Math.round(cardHeight * -0.45); // Maintain proportional overlap

    // Calculate container dimensions based on 10-column grid
    const columns = 10;
    const totalCards = 78;
    const totalRows = Math.ceil(totalCards / columns);

    const containerWidth = (columns - 1) * (cardWidth + overlapX) + cardWidth;
    const containerHeight = (totalRows - 1) * (cardHeight + overlapY) + cardHeight + 50; // +50 for selected card lift and extra space
    const containerPadding = 20; // Padding on each side to prevent cropping

    const handleCardClick = (cardId: number) => {
        if (isComplete) return; // Don't allow changes after completion

        setSelectedCards(prev => {
            const newSet = new Set(prev);
            if (newSet.has(cardId)) {
                newSet.delete(cardId);
                setIsComplete(false);
                // Remove reversed state when deselecting
                setCardReversedState(prevState => {
                    const newState = new Map(prevState);
                    newState.delete(cardId);
                    return newState;
                });
                // Track card deselection
                track('card_deselected', {
                    cardId,
                    remainingSelections: newSet.size,
                    maxSelections
                });
            } else if (newSet.size < maxSelections) {
                newSet.add(cardId);
                // Randomly decide if card is reversed when selected (10% chance)
                const isReversed = Math.random() < 0.1;
                setCardReversedState(prevState => {
                    const newState = new Map(prevState);
                    newState.set(cardId, isReversed);
                    return newState;
                });

                // Track card selection
                track('card_selected', {
                    cardId,
                    currentSelections: newSet.size,
                    maxSelections,
                    isReversed
                });

                // Check if we've reached max selections
                if (newSet.size === maxSelections && onSelectionComplete) {
                    setIsComplete(true);
                    // Convert Set to ordered array with complete TarotCard objects
                    const selectedArray = Array.from(newSet).map(id => {
                        const card = getTranslatedCard(id);
                        const reversed = cardReversedState.get(id) ?? isReversed;
                        return {
                            ...card,
                            reversed,
                            visible: true
                        };
                    });

                    // Track selection complete
                    track('card_selection_complete', {
                        selectedCards: Array.from(newSet),
                        maxSelections,
                        reversedCards: Array.from(newSet).filter(id => cardReversedState.get(id))
                    });

                    // Wait 1 second then call callback
                    setTimeout(() => {
                        onSelectionComplete(selectedArray);
                    }, 500);
                }
            }
            return newSet;
        });
    };

    const shuffleCards = () => {
        setIsShuffling(true);
        track('cards_shuffled', {
            currentSelections: selectedCards.size,
            maxSelections
        });
        setCardOrder(prevCards => {
            const newCards = [...prevCards];
            // Fisher-Yates shuffle algorithm
            for (let i = newCards.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [newCards[i], newCards[j]] = [newCards[j], newCards[i]];
            }
            return newCards;
        });
        setTimeout(() => setIsShuffling(false), 800);
    };

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Shuffle cards once on component mount
    useEffect(() => {
        track('card_selection_opened', {
            maxSelections
        });
        shuffleCards();
    }, []);

    return (
        <div className="flex flex-col h-screen ">
            {/* Title - Fixed height */}
            <div className="w-full text-center py-4 flex-shrink-0" style={{ height: '10vh' }}>
                <h1 className="text-2xl font-bold text-[#2B2E4A] mb-1" style={{ fontFamily: '"Playfair Display", serif' }}>
                    {t('cardSelection.title')}
                </h1>
                <p className="text-sm text-[#2B2E4A]/70">
                    {t('cardSelection.cardsSelected', { count: selectedCards.size, max: maxSelections })}
                </p>
            </div>

            {/* Cards Grid - Scrollable middle section */}
            <div className="flex-1 w-full flex justify-center overflow-y-auto overflow-x-auto" style={{ height: '75vh' }}>
                <div
                    className="relative"
                    style={{
                        width: `${containerWidth + containerPadding * 2}px`,
                        height: `${containerHeight}px`,
                        paddingTop: `${containerPadding}px`,
                        paddingBottom: `${containerPadding}px`,
                        paddingLeft: `${containerPadding}px`,
                        paddingRight: `${containerPadding}px`
                    }}
                >
                    {cardOrder.map((cardId, index) => {
                        const card = cards.find(c => c.id === cardId);
                        if (!card) return null;

                        const isSelected = selectedCards.has(cardId);
                        const maxReached = selectedCards.size === maxSelections;
                        const shouldFlip = isSelected && maxReached;

                        // Calculate position in 10-column grid with overlap
                        const col = index % columns;
                        const row = Math.floor(index / columns);

                        const x = col * (cardWidth + overlapX);
                        const y = row * (cardHeight + overlapY);

                        return (
                            <motion.div
                                key={cardId}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{
                                    x: x,
                                    y: y + (isSelected ? -10 : 0),
                                    opacity: 1,
                                    scale: isSelected ? 1.05 : 1,
                                }}
                                transition={{
                                    duration: 0.8,
                                    ease: "easeInOut",
                                }}
                                style={{
                                    position: 'absolute',
                                    width: cardWidth,
                                    height: cardHeight,
                                    zIndex: isSelected ? 100 : 78 - index,
                                }}
                                className={`cursor-pointer ${isSelected ? 'drop-shadow-2xl' : ''}`}
                                onClick={() => handleCardClick(cardId)}
                            >
                                <div className={`relative ${isSelected ? 'ring-4 ring-yellow-400 rounded-lg' : ''}`}>
                                    <CardImage2
                                        id={card.id}
                                        visible={true}
                                        width={cardWidth}
                                        reversed={false}
                                        flipped={true}
                                        showFront={shouldFlip}
                                        description={{
                                            ...card,
                                            card_name: getCardName(card.id, card.card_name || '',

                                            )
                                        }}
                                    />
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* Bottom Shuffle Button - Fixed at bottom */}
            <div className="w-full px-4 flex-shrink-0 border-t border-gray-100 flex items-end pb-8" style={{ height: '15vh' }}>
                <Button
                    variant="contained"
                    fullWidth
                    size="small"
                    onClick={shuffleCards}
                    disabled={isShuffling || isComplete}
                    sx={{
                        fontFamily: '"Playfair Display", serif',
                        fontSize: '1.1rem',

                        textTransform: 'none',

                    }}
                >
                    <Shuffle className="w-5 h-5 mr-3" />
                    {isComplete ? t('cardSelection.selectionComplete') : isShuffling ? t('cardSelection.shuffling') : t('cardSelection.shuffleCards')}
                </Button>
            </div>

        </div>
    );
};

export default CardSelection;